/**
 * PatientCheckin.jsx
 * src/pages/assistant/PatientCheckin.jsx
 * Fully dynamic — fetches today's appointments from DB and handles check-in/no-show
 */

import { useState, useEffect, useCallback } from "react";
import { Avatar, PageHeader, Loading, EmptyState, useConfirm, ConfirmDialog, apiFetch } from "../../components/helpers";

const STATUS_COLOR = {
  Pending:      ["#FFF8F0", "#D85A30"],
  Waiting:      ["#E6F1FB", "#042C53"],
  "In Progress":["#FAEEDA", "#633806"],
  Completed:    ["#E1F5EE", "#085041"],
  Cancelled:    ["#FCEBEB", "#501313"],
  Blocked:      ["#F0F4F8", "#6B7A8D"],
};

const TODAY = new Date().toISOString().split("T")[0];

function TimeBadge({ time }) {
  if (!time) return null;
  return (
    <span style={{ fontFamily: "monospace", fontSize: 13, fontWeight: 700, color: "#0A1628",
      background: "#F0F4F8", padding: "2px 8px", borderRadius: 8 }}>
      🕐 {time.slice(0,5)}
    </span>
  );
}

function StatusPill({ status }) {
  const [bg, color] = STATUS_COLOR[status] || STATUS_COLOR.Pending;
  return (
    <span style={{ background: bg, color, fontSize: 11, fontWeight: 700,
      padding: "3px 10px", borderRadius: 20 }}>
      {status}
    </span>
  );
}

export default function PatientCheckin() {
  const [items,      setItems]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [flash,      setFlash]      = useState({ msg: "", type: "success" });
  const [actionLoading, setActionLoading] = useState(null);
  const { confirm, confirmProps }   = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const json = await apiFetch(`get_todays_appointments.php?date=${TODAY}`);
      if (json.status) setItems(json.data || []);
      else setItems([]);
    } catch { setItems([]); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const showFlash = (msg, type = "success") => {
    setFlash({ msg, type });
    setTimeout(() => setFlash({ msg: "", type: "success" }), 3500);
  };

  // ── Check In (move to Waiting)
  const handleCheckin = async (item) => {
    const ok = await confirm({
      title: `Check-in: ${item.patient_name}`,
      message: `Confirm check-in for ${item.patient_name} — appointment with ${item.doctor_name} at ${item.appointment_time?.slice(0,5)}?`,
      confirmText: "Yes, Check In",
      variant: "primary",
      icon: "✅",
    });
    if (!ok) return;
    setActionLoading(item.schedule_id);
    try {
      const json = await apiFetch("update_appointment_status.php", {
        method: "POST",
        body: { appointment_id: item.schedule_id, status: "Waiting" }
      });
      if (json.status) {
        showFlash(`${item.patient_name} checked in! Status → Waiting.`);
        load();
      } else {
        showFlash(json.message || "Check-in failed", "danger");
      }
    } catch { showFlash("Network error. Please try again.", "danger"); }
    setActionLoading(null);
  };

  // ── Mark In Progress
  const handleInProgress = async (item) => {
    const ok = await confirm({
      title: "Start Treatment?",
      message: `Mark ${item.patient_name} as 'In Progress' (treatment started)?`,
      confirmText: "Start",
      variant: "warning",
      icon: "🦷",
    });
    if (!ok) return;
    setActionLoading(item.schedule_id);
    try {
      const json = await apiFetch("update_appointment_status.php", {
        method: "POST",
        body: { appointment_id: item.schedule_id, status: "In Progress" }
      });
      if (json.status) { showFlash(`${item.patient_name} — treatment in progress.`); load(); }
    } catch { showFlash("Network error", "danger"); }
    setActionLoading(null);
  };

  // ── No Show (Cancelled)
  const handleNoShow = async (item) => {
    const ok = await confirm({
      title: "Mark as No-Show",
      message: `Mark ${item.patient_name} as no-show and cancel the appointment?`,
      confirmText: "Mark No-Show",
      variant: "warning",
      icon: "⚠️",
    });
    if (!ok) return;
    setActionLoading(item.schedule_id);
    try {
      const json = await apiFetch("update_appointment_status.php", {
        method: "POST",
        body: { appointment_id: item.schedule_id, status: "Cancelled" }
      });
      if (json.status) { showFlash(`${item.patient_name} marked as no-show.`, "warning"); load(); }
    } catch { showFlash("Network error", "danger"); }
    setActionLoading(null);
  };

  // Stats
  const total      = items.length;
  const checkedIn  = items.filter(i => i.status !== "Pending" && i.status !== "Cancelled").length;
  const pending    = items.filter(i => i.status === "Pending").length;
  const waiting    = items.filter(i => i.status === "Waiting").length;
  const inProgress = items.filter(i => i.status === "In Progress").length;
  const completed  = items.filter(i => i.status === "Completed").length;
  const cancelled  = items.filter(i => i.status === "Cancelled").length;
  const progress   = total > 0 ? Math.round((completed / total) * 100) : 0;

  const filtered = items.filter(a =>
    (filterStatus === "All" || a.status === filterStatus) &&
    (
      a.patient_name?.toLowerCase().includes(search.toLowerCase()) ||
      a.doctor_name?.toLowerCase().includes(search.toLowerCase()) ||
      a.treatment?.toLowerCase().includes(search.toLowerCase())
    )
  );

  const dateLabel = new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="p-3 p-lg-4" style={{ minHeight: "100vh", fontFamily: "'Nunito','Segoe UI',sans-serif" }}>
      <ConfirmDialog {...confirmProps} />

      <PageHeader title="Patient Check-in" subtitle={`Today — ${dateLabel}`}>
        <select className="form-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ width: 140, fontSize: 13 }}>
          {["All","Pending","Waiting","In Progress","Completed","Cancelled"].map(s =>
            <option key={s}>{s}</option>
          )}
        </select>
        <input className="form-control" placeholder="Search patient or doctor..." value={search}
          onChange={e => setSearch(e.target.value)} style={{ width: 220, fontSize: 13 }} />
        <button className="btn btn-light fw-semibold" onClick={load} style={{ fontSize: 13 }}>↻ Refresh</button>
      </PageHeader>

      {/* Stats cards */}
      <div className="row g-3 mb-4">
        {[
          ["Total Today",   total,      "#D85A30"],
          ["Pending",       pending,    "#6B7A8D"],
          ["Waiting",       waiting,    "#378ADD"],
          ["In Progress",   inProgress, "#F6A623"],
          ["Completed",     completed,  "#1D9E75"],
          ["No Show",       cancelled,  "#E24B4A"],
        ].map(([label, val, accent]) => (
          <div className="col-6 col-md-4 col-xl-2" key={label}>
            <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 14, borderLeft: `3px solid ${accent}` }}>
              <div className="card-body p-3">
                <p className="text-muted fw-bold mb-1" style={{ fontSize: 10, letterSpacing: ".6px", textTransform: "uppercase" }}>{label}</p>
                <h3 className="fw-bold mb-0" style={{ fontSize: 24, color: "#0A1628" }}>{val}</h3>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Progress bar */}
      {total > 0 && (
        <div className="card border-0 shadow-sm mb-4 p-3" style={{ borderRadius: 14 }}>
          <div className="d-flex justify-content-between mb-1">
            <span className="fw-bold" style={{ fontSize: 12, color: "#0A1628" }}>Completion Progress</span>
            <span className="text-muted" style={{ fontSize: 12 }}>{completed} of {total} completed ({progress}%)</span>
          </div>
          <div className="progress" style={{ height: 10, borderRadius: 8 }}>
            <div className="progress-bar"
              style={{ width: `${progress}%`, background: "#1D9E75", borderRadius: 8, transition: "width .8s ease" }} />
          </div>
        </div>
      )}

      {/* Flash */}
      {flash.msg && (
        <div className={`alert alert-${flash.type === "danger" ? "danger" : flash.type === "warning" ? "warning" : "success"} py-2 px-3 mb-3`}
          style={{ fontSize: 13 }}>
          {flash.msg}
        </div>
      )}

      {/* Cards */}
      {loading ? <Loading /> : filtered.length === 0 ? (
        <EmptyState message={search ? "No patients match your search." : "No appointments for today."} />
      ) : (
        <div className="row g-3">
          {filtered.map((a) => {
            const [bgColor, textColor] = STATUS_COLOR[a.status] || STATUS_COLOR.Pending;
            const isLoading = actionLoading === a.schedule_id;
            return (
              <div className="col-12 col-md-6 col-xl-4" key={a.schedule_id}>
                <div className="card border-0 shadow-sm h-100"
                  style={{ borderRadius: 16, borderTop: `3px solid ${textColor}` }}>
                  <div className="card-body p-3">

                    {/* Header row */}
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="d-flex align-items-center gap-2">
                        <Avatar name={a.patient_name} size={42} />
                        <div>
                          <div className="fw-bold" style={{ fontSize: 14, color: "#0A1628" }}>{a.patient_name || "—"}</div>
                          <div style={{ fontSize: 11, color: "#D85A30", fontWeight: 600 }}>Dr. {a.doctor_name}</div>
                        </div>
                      </div>
                      <TimeBadge time={a.appointment_time} />
                    </div>

                    {/* Treatment + status */}
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <span className="text-muted" style={{ fontSize: 12 }}>
                        🦷 {a.treatment || "General Checkup"}
                      </span>
                      <StatusPill status={a.status} />
                    </div>

                    {/* Mobile if available */}
                    {a.mobile && (
                      <div className="mb-3" style={{ fontSize: 11, color: "#6B7A8D" }}>
                        📱 {a.mobile}
                      </div>
                    )}

                    {/* Action buttons per status */}
                    <div className="d-flex gap-2 mt-auto">
                      {a.status === "Pending" && (
                        <>
                          <button
                            className="btn btn-sm fw-bold text-white flex-grow-1"
                            style={{ background: "#D85A30", borderRadius: 8, fontSize: 12 }}
                            onClick={() => handleCheckin(a)}
                            disabled={isLoading}
                          >
                            {isLoading ? <span className="spinner-border spinner-border-sm" /> : "✅ Check In"}
                          </button>
                          <button
                            className="btn btn-sm fw-bold"
                            style={{ background: "#FCEBEB", color: "#501313", borderRadius: 8, fontSize: 12 }}
                            onClick={() => handleNoShow(a)}
                            disabled={isLoading}
                          >
                            No Show
                          </button>
                        </>
                      )}

                      {a.status === "Waiting" && (
                        <button
                          className="btn btn-sm fw-bold text-white flex-grow-1"
                          style={{ background: "#F6A623", borderRadius: 8, fontSize: 12 }}
                          onClick={() => handleInProgress(a)}
                          disabled={isLoading}
                        >
                          {isLoading ? <span className="spinner-border spinner-border-sm" /> : "🦷 Start Treatment"}
                        </button>
                      )}

                      {a.status === "In Progress" && (
                        <div className="w-100 text-center py-2 rounded-2" style={{ background: "#FAEEDA", fontSize: 12, fontWeight: 600, color: "#633806" }}>
                          ⏳ Treatment In Progress
                        </div>
                      )}

                      {a.status === "Completed" && (
                        <div className="w-100 text-center py-2 rounded-2" style={{ background: "#E1F5EE", fontSize: 12, fontWeight: 600, color: "#085041" }}>
                          ✓ Treatment Completed
                        </div>
                      )}

                      {a.status === "Cancelled" && (
                        <div className="w-100 text-center py-2 rounded-2" style={{ background: "#FCEBEB", fontSize: 12, fontWeight: 600, color: "#501313" }}>
                          ✗ No Show / Cancelled
                        </div>
                      )}
                    </div>

                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}